/**
 * The account menu's two small dialogs — set a password, change it — in this app's copy.
 * The dialog is the design system's; this is the wiring to the provider and the lines.
 */

import { PasswordDialog } from '@atlas-nook/design-system';
import type { PasswordDialogStrings } from '@atlas-nook/design-system';
import type { JSX } from 'react';

import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { useAuth } from './AuthProvider.js';
import { classifyAuthError } from './errors.js';
import type { AuthProblemKind } from './errors.js';

const PROBLEM_COPY: Partial<Record<AuthProblemKind, string>> = {
  badCredentials: S.password.errCurrent,
  weakPassword: S.signin.errWeakPassword,
  tooMany: S.signin.errTooMany,
  network: S.signin.errNetwork,
  popup: S.signin.errPopup,
  recentLogin: S.password.errRecentLogin,
};

export function describePasswordError(error: unknown): string {
  return PROBLEM_COPY[classifyAuthError(error).kind] ?? S.signin.failed;
}

export type PasswordDialogKind = 'set' | 'change' | null;

const COMMON = {
  current: S.password.current,
  next: S.password.next,
  nextHelp: S.password.nextHelp,
  required: S.password.required,
  tooShort: S.signin.passwordTooShort,
  confirm: S.password.confirm,
  cancel: S.password.cancel,
  working: S.password.working,
};

const CHANGE: PasswordDialogStrings = { ...COMMON, title: S.password.changeTitle };
const SET: PasswordDialogStrings = {
  ...COMMON,
  title: S.password.setTitle,
  body: S.password.setBody,
};

export function PasswordDialogs({
  open,
  onClose,
}: {
  open: PasswordDialogKind;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const { setPassword, changePassword } = useAuth();
  const describe = async (error: unknown): Promise<string> => describePasswordError(error);

  return (
    <>
      <PasswordDialog
        open={open === 'change'}
        onClose={onClose}
        strings={CHANGE}
        askCurrent
        onSubmit={async (current, next) => {
          await changePassword(current ?? '', next);
          toast.show(S.password.changed);
        }}
        describe={describe}
      />
      <PasswordDialog
        open={open === 'set'}
        onClose={onClose}
        strings={SET}
        askCurrent={false}
        onSubmit={async (_current, next) => {
          await setPassword(next);
          toast.show(S.password.set);
        }}
        describe={describe}
      />
    </>
  );
}
